import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { NavLink } from 'react-router-dom';
import { Box } from '@mui/material';
import styled from '@emotion/styled';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import { selectIsUserAdmin, selectUserRoles } from '@store/auth';

import AlertLogo from '@assets/images/smart-alerts/alerts.png';
import ConversionRatesLogo from '@assets/images/conversion-rates/exchange-rate.png';
import K8SClusterLogo from '@assets/images/dashboard/shortcuts/k8s-cluster.svg';
import PaymentOptionLogo from '@assets/images/payment-options/option.svg';
import PaymentConfigLogo from '@assets/images/payment-config/paymentConfig.png';
import UploadLogo from '@assets/images/dashboard/uploads-image.png';
import MonitoringDashboardLogo from '@assets/images/monitoring-dashboard/module-logo.png';
import SonarQubeLogo from '@assets/images/dashboard/shortcuts/sonarqube.png';
import Search from '@assets/images/payment-search/search.png';
import UserManagement from '@assets/images/user-management/user.jpg';
import LegacyPaymentOptions from '@assets/images/legacy-payment-options/legacy-payment-options.png';
import PaymentOrchestrationLogo from '@assets/images/payment-orchestration/orchestration.svg';

import { HEADER_HEIGHT } from '@dashboardComponents/ApplicationTemplate/ApplicationTemplate';
import GrafanaPanel from '../../../../components/GrafanaPanel';
import LoggedInPageTemplate from '../../components/LoggedInPageTemplate/LoggedInPageTemplate';

import { verifyUserAccess } from '../../../../utils/utils';

import { ROLES } from '../../../../app/App.constants';

const ShortcutWrapper = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px;
  color: #374151;
  cursor: pointer;
  text-decoration: none;
  height: calc(100% - 20px);

  [class~='MuiTypography-h6'] {
    font-size: 18px;
  }

  [class~='MuiTypography-subtitle1'] {
    font-size: 10px;
  }

  &[disabled] {
    pointer-events: none;
  }

  &:not([disabled]):hover {
    background-color: #f9fafb;
  }
`;

const ItemWrapper = styled(NavLink)`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px;
  color: #374151;
  text-decoration: none;
  height: calc(100% - 20px);

  &:hover {
    background-color: #f9fafb;
  }

  & a[disabled] {
    pointer-events: none;
  }

  [class~='MuiTypography-h6'] {
    font-size: 18px;
  }

  [class~='MuiTypography-subtitle1'] {
    font-size: 11px;
  }
`;

const LogoWrapper = styled.div`
  position: relative;
  top: 5%;
  width: 100%;
  height: 50%;
  display: flex;
  justify-content: center;
  margin: 0 0 30px 0;
`;

const Logo = styled.img`
  height: 100%;
`;

const modulesOptions = [
  {
    logoPath: PaymentConfigLogo,
    name: 'Payment Configuration',
    description: 'Manage global payment settings',
    path: 'payment-config',
    roles: [
      ROLES.ROLE_ADMIN,
      ROLES.ROLE_PAYMENT_CONFIGURATIONS_VIEW,
      ROLES.ROLE_PAYMENT_CONFIGURATIONS_EDIT,
      ROLES.ROLE_CHANNEL_HASH_VIEW,
      ROLES.ROLE_CHANNEL_HASH_EDIT,
      ROLES.ROLE_TOGGLZ_VIEW,
      ROLES.ROLE_TOGGLZ_EDIT,
      ROLES.ROLE_CURRENCY_DETAILS_EDIT,
      ROLES.ROLE_CURRENCY_DETAILS_VIEW,
    ],
  },
  {
    logoPath: PaymentOptionLogo,
    name: 'Payment Options',
    description: 'Manage LOB-specific configurations and gateway settings',
    path: 'payment-options',
    roles: [ROLES.ROLE_ADMIN, ROLES.ROLE_PAYOPT_VIEW, ROLES.ROLE_PAYOPT_EDIT],
  },
  {
    logoPath: PaymentOrchestrationLogo,
    name: 'Payment Orchestration',
    description: 'Manage configurations for payment orchestration service',
    path: 'payment-orchestration',
    roles: [
      ROLES.ROLE_ADMIN,
      ROLES.ROLE_RULE_VIEW,
      ROLES.ROLE_RULE_EDIT,
      ROLES.ROLE_SOURCE_VIEW,
      ROLES.ROLE_SOURCE_EDIT,
    ],
  },
  {
    logoPath: LegacyPaymentOptions,
    name: 'Legacy Payment Configuration',
    description: 'Manage Legacy Payments related configurations',
    path: 'legacy-payment-options',
    roles: [
      ROLES.ROLE_LEGACY_ADMIN,
      ROLES.ROLE_LEGACY_MAINTENANCE_SCHEDULE_EDIT,
      ROLES.ROLE_LEGACY_MAINTENANCE_SCHEDULE_VIEW,
      ROLES.ROLE_LEGACY_CONTEXTUAL_MESSAGES_EDIT,
      ROLES.ROLE_LEGACY_CONTEXTUAL_MESSAGES_VIEW,
    ],
  },
  {
    logoPath: Search,
    name: 'Order and Payment Search',
    description: 'Look up order, transaction, and refund details',
    path: 'payment-search',
    roles: [ROLES.ROLE_ADMIN, ROLES.ROLE_ORDER_SEARCH_VIEW],
  },
  {
    logoPath: UserManagement,
    name: 'User Management',
    description: 'Manage Users and their roles',
    path: 'user-management',
    roles: [
      ROLES.ROLE_ADMIN,
      ROLES.ROLE_USER_VIEW,
      ROLES.ROLE_USER_EDIT,
      ROLES.ROLE_USER_ROLE_VIEW,
      ROLES.ROLE_USER_ROLE_EDIT,
    ],
  },
  {
    logoPath: UploadLogo,
    name: 'Uploads',
    description: 'Upload images to GCP Payments Bucket',
    path: 'uploads',
    roles: [ROLES.ROLE_ADMIN, ROLES.ROLE_UPLOADS],
  },
  {
    logoPath: MonitoringDashboardLogo,
    name: 'Monitoring Dashboard',
    description: 'Monitor Payment Services Through Logs, DB and Other Sources',
    path: 'monitoring-dashboard',
    roles: [
      ROLES.ROLE_ADMIN,
      ROLES.ROLE_MONITORING_MAIN_DASHBOARD,
      ROLES.ROLE_MONITORING_LOG_EXPLORER,
      ROLES.ROLE_MONITORING_LOAD_BALANCER_DASHBOARD,
      ROLES.ROLE_MONITORING_LOAD_BALANCER_LOGS,
      ROLES.ROLE_MONITORING_HEALTH_CHECK,
      ROLES.ROLE_MONITORING_GALAXY_TRANSACTIONS,
      ROLES.ROLE_MONITORING_CASHIER_TRANSACTIONS,
      ROLES.ROLE_MONITORING_GALAXY_TRANSACTIONS,
    ],
  },
  {
    logoPath: AlertLogo,
    name: 'Smart Alerts',
    description:
      'Configure system wide alerts based on different sources for data',
    path: 'smart-alerts',
    roles: [ROLES.ROLE_ADMIN, ROLES.ROLE_SMART_ALERTS],
  },

  {
    logoPath: ConversionRatesLogo,
    name: 'Conversion Rates',
    description: 'Manage Currency and Loyalty Rates Conversion',
    path: 'conversion-rates',
    roles: [
      ROLES.ROLE_ADMIN,
      ROLES.ROLE_MCC_EDIT,
      ROLES.ROLE_MCC_VIEW,
      ROLES.ROLE_LOYALTY_EDIT,
      ROLES.ROLE_LOYALTY_VIEW,
    ],
  },
  {
    logoPath: SonarQubeLogo,
    name: 'Cashier On SonarQuebe',
    description: 'Code Quality Analysis using SonarQuebe',
    path: 'https://sq.airasiatech.com/dashboard?id=cashier-service',
    roles: [ROLES.ROLE_ADMIN],
  },
  {
    logoPath: K8SClusterLogo,
    name: 'K8S Cluster',
    description:
      'K8S Cluster details from number of nodes and the specifications of each node',
    path: 'https://console.cloud.google.com/monitoring/dashboards/resourceList/kubernetes?project=airasia-oms-prd&pageState=(%22interval%22:(),%22gkeTableState%22:(),%22filter%22:())',
    roles: [ROLES.ROLE_ADMIN],
  },
  {
    logoPath: K8SClusterLogo,
    name: 'K8S Resources Dashboard',
    description:
      'Monitoring K8S Nodes Performance from CPU & Memory utilization',
    path: 'https://console.cloud.google.com/monitoring/dashboards/builder/c17a6e39-ba7a-484c-8453-34e1dd5bc105?project=airasia-payment-prd&pageState=(%22interval%22:(),%22gkeTableState%22:())&dashboardBuilderState=%257B%2522editModeEnabled%2522:false%257D&timeDomain=1w',
    roles: [ROLES.ROLE_ADMIN],
  },
  {
    logoPath: K8SClusterLogo,
    name: 'K8S Workload Status',
    description: 'The current status of deployed services resource utilization',
    path: 'https://console.cloud.google.com/kubernetes/workload/cost?project=airasia-payment-prd',
    roles: [ROLES.ROLE_ADMIN],
  },
];

function generateGrafanaURL() {
  const currentTime = new Date().getTime();
  return `${process.env.REACT_APP_GRAFANA_LANDING_PAGE}&from=now-24h&to=now&panelId=1&page=LANDING_PAGE&theme=light&env=${process.env.REACT_APP_ENV}&js_file=${process.env.REACT_APP_URL}/grafana-cf/customize.js%3Fx%3D${currentTime}`;
}

function Landing() {
  const userRoles = useSelector(selectUserRoles);
  const isAdminUser = useSelector(selectIsUserAdmin);

  const checkUserAccess = useCallback(
    (featureRoles) => isAdminUser || verifyUserAccess(userRoles, featureRoles),
    [isAdminUser, userRoles],
  );

  return (
    <LoggedInPageTemplate>
      <Container
        style={{
          width: '100%',
          maxWidth: '100%',
          height: `calc(100vh - ${HEADER_HEIGHT}px)`,
          minHeight: `calc(100vh - ${HEADER_HEIGHT}px)`,
          overflow: 'scroll',
        }}>
        <Grid
          container
          spacing={3}
          justifyContent="flex-start"
          style={{
            minWidth: '800px',
            maxWidth: '1200px',
            margin: '0px auto',
            padding: '0 0 20px 0',
          }}>
          <Grid
            item
            xs={12}
            style={{ width: '100%', textAlign: 'center', marginTop: 50 }}>
            <Typography
              variant="h1"
              component="div"
              gutterBottom>
              Payment Dashboard
            </Typography>
            <Typography
              variant="subtitle1"
              gutterBottom
              component="div">
              Manage all payments operations with seamless integration
            </Typography>
          </Grid>
          {modulesOptions.map((option) => {
            const hasAccess = checkUserAccess(option.roles);

            if (!hasAccess) return null;

            return (
              <Grid
                key={option.name}
                item
                xs={3}
                style={{ width: '100%', textAlign: 'center' }}>
                <Paper
                  elevation={hasAccess ? 3 : 0}
                  style={{
                    width: '100%',
                    height: '250px',
                  }}>
                  {!option.path.startsWith('http') && (
                    <ItemWrapper
                      className={hasAccess ? 'enabled' : 'disabled'}
                      to={`/${option.path}`}
                      onClick={(event) => {
                        if (!hasAccess) {
                          event.preventDefault();
                        }
                      }}>
                      <LogoWrapper>
                        <Logo
                          src={option.logoPath}
                          alt={option.logoPath}
                        />
                      </LogoWrapper>
                      <Typography
                        variant="h6"
                        gutterBottom
                        component="div">
                        {option.name}
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        gutterBottom
                        component="div">
                        {option.description}
                      </Typography>
                    </ItemWrapper>
                  )}
                  {option.path.startsWith('http') && (
                    <ShortcutWrapper
                      href={option.path}
                      disabled={!hasAccess}
                      target="_blank">
                      <LogoWrapper>
                        <Logo
                          src={option.logoPath}
                          alt={option.logoPath}
                        />
                      </LogoWrapper>
                      <Typography
                        variant="h6"
                        gutterBottom
                        component="div">
                        {option.name}
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        gutterBottom
                        component="div">
                        {option.description}
                      </Typography>
                      <Box
                        sx={{
                          position: 'relative',
                          height: 'calc(100% - 180px)',
                          marginTop: '30px',
                        }}>
                        <GrafanaPanel url={generateGrafanaURL()} />
                      </Box>
                    </ShortcutWrapper>
                  )}
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </LoggedInPageTemplate>
  );
}

export default Landing;
